import type { AssignmentFolder, Classroom, HomeworkAssignment } from "./types.js";

const defaults: AssignmentFolder[] = [
  { id: "homework", name: "功課" },
  { id: "assessment", name: "測驗" },
  { id: "dictation", name: "默書" },
];

export type AssignmentFolderAction =
  | { type: "create"; folder: AssignmentFolder }
  | { type: "rename"; id: string; name: string }
  | { type: "delete"; id: string };

export function assignmentFolders(classroom: Pick<Classroom, "assignmentFolders">): AssignmentFolder[] {
  return (classroom.assignmentFolders ?? defaults).map((folder) => ({ ...folder }));
}

export function assignmentFolderId(assignment: HomeworkAssignment, folders: AssignmentFolder[]): string {
  // A removed folder never hides its records or changes their assessment semantics.
  const id = assignment.folderId ?? assignment.category;
  return id && folders.some((folder) => folder.id === id) ? id : "uncategorized";
}

export function applyAssignmentFolderAction(
  classroom: Pick<Classroom, "assignmentFolders">,
  action: AssignmentFolderAction,
): AssignmentFolder[] {
  const folders = assignmentFolders(classroom);
  const id = action.type === "create" ? action.folder.id : action.id;
  if (!id || id === "uncategorized") throw new Error("invalid-folder");
  const existing = folders.find((folder) => folder.id === id);
  if (action.type === "create" ? !!existing : !existing) throw new Error("stale-folder");
  if (action.type === "delete") return folders.filter((folder) => folder.id !== id);
  const name = (action.type === "create" ? action.folder.name : action.name).trim();
  if (!name || name.length > 50) throw new Error("invalid-folder-name");
  if (
    name === "未分類" ||
    folders.some((folder) => folder.id !== id && folder.name.toLowerCase() === name.toLowerCase())
  ) {
    throw new Error("duplicate-folder-name");
  }
  return action.type === "create"
    ? [...folders, { id, name }]
    : folders.map((folder) => (folder.id === id ? { ...folder, name } : folder));
}

export function isValidAssignmentFolders(value: unknown): value is AssignmentFolder[] {
  if (!Array.isArray(value)) return false;
  const ids = new Set<string>();
  const names = new Set<string>();
  return value.every((folder) => {
    if (
      !folder ||
      typeof folder !== "object" ||
      typeof folder.id !== "string" ||
      !folder.id ||
      folder.id === "uncategorized" ||
      typeof folder.name !== "string" ||
      !folder.name.trim() ||
      folder.name.length > 50 ||
      folder.name === "未分類"
    )
      return false;
    const name = folder.name.trim().toLowerCase();
    if (ids.has(folder.id) || names.has(name)) return false;
    ids.add(folder.id);
    names.add(name);
    return true;
  });
}
